import { useContext, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { API } from "../../../api";
import { MissionControlLog, TeamMember } from "../../../models";
import { UserContext } from "../../../hooks/UserContext";
import { CSV } from "@rogoag/shared-ts";

export const useMissionControlLogsViewModel = () => {
	const currentUser = useContext(UserContext);
	const isSuperUser = currentUser?.isAdmin ?? false;

	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [selectedUser, setSelectedUser] = useState<TeamMember | null>(null);
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [pastHours, setPastHours] = useState<number>(1);
	const [loading, setLoading] = useState(false);
	const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	useEffect(() => {
		if (isSuperUser) {
			fetchTeamMembers();
		}
	}, [isSuperUser]);

	// Set default date range to past 1 hour on mount
	useEffect(() => {
		const now = dayjs();
		setEndDate(now);
		setStartDate(now.subtract(1, "hour"));
	}, []);

	const handlePastHoursChange = (hours: number) => {
		if (hours > 0) {
			setPastHours(hours);
			const now = dayjs();
			setEndDate(now);
			setStartDate(now.subtract(hours, "hour"));
			clearMessages();
		}
	};

	const fetchTeamMembers = async () => {
		setLoadingTeamMembers(true);
		setError(null);
		try {
			// Fetch team members with missionControlUsername not empty (server-side filter)
			const members = await API.Data.TeamMembers.get.byFilter({
				fields: {
					missionControlUsername: { operator: "is_not_empty" },
				},
			});
			members.sort((a, b) => a.name.localeCompare(b.name));
			setTeamMembers(members);
		} catch (err: any) {
			setError(`Failed to load team members: ${err.message || "Unknown error"}`);
		} finally {
			setLoadingTeamMembers(false);
		}
	};

	const downloadJson = (data: MissionControlLog[], filename: string) => {
		const json = JSON.stringify(data, null, 2);
		const blob = new Blob([json], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		link.click();
		URL.revokeObjectURL(url);
	};

	const downloadCsv = (data: MissionControlLog[], filename: string) => {
		// Convert MissionControlLog objects to flat JSON objects for CSV
		const flattenedData = data.map((log) => ({
			userId: log.userId || "",
			timestamp: log.timestamp || "",
			eventType: log.eventType || "",
			// Add any other fields from MissionControlLog as needed
		}));

		const csv = CSV.fromJSON(flattenedData);
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		link.click();
		URL.revokeObjectURL(url);
	};

	const downloadTsv = (data: MissionControlLog[], filename: string) => {
		// Convert MissionControlLog objects to flat JSON objects for CSV, then to TSV
		const flattenedData = data.map((log) => ({
			userId: log.userId || "",
			timestamp: log.timestamp || "",
			eventType: log.eventType || "",
			// Add any other fields from MissionControlLog as needed
		}));

		const csv = CSV.fromJSON(flattenedData);
		const tsv = CSV.toTSV(csv);
		const blob = new Blob([tsv], { type: "text/tab-separated-values" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		link.click();
		URL.revokeObjectURL(url);
	};

	const handleDownload = async (format: "json" | "csv" | "tsv" = "json") => {
		if (!selectedUser || !startDate || !endDate) {
			setError("Please select a user and date range");
			return;
		}

		if (endDate < startDate) {
			setError("End date must be after start date");
			return;
		}

		setLoading(true);
		setError(null);
		setSuccessMessage(null);

		try {
			const startDateStr = startDate.toISOString();
			const endDateStr = endDate.toISOString();

			const logs = await API.MissionControl.getLogs(selectedUser.missionControlUsername, startDateStr, endDateStr);

			if (logs.length === 0) {
				setError("No logs found for the selected criteria");
				return;
			}

			const userName = selectedUser.name?.replace(/\s+/g, "_") || selectedUser.missionControlUsername;
			const startDateFormatted = startDate.format("YYYY-MM-DD_HH-mm");
			const endDateFormatted = endDate.format("YYYY-MM-DD_HH-mm");
			const extension = format === "csv" ? "csv" : format === "tsv" ? "tsv" : "json";
			const filename = `mission-control-logs_${userName}_${startDateFormatted}_${endDateFormatted}.${extension}`;

			if (format === "csv") {
				downloadCsv(logs, filename);
			} else if (format === "tsv") {
				downloadTsv(logs, filename);
			} else {
				downloadJson(logs, filename);
			}

			setSuccessMessage(`Successfully downloaded ${logs.length} log entries`);
		} catch (err: any) {
			setError(`Failed to download logs: ${err.message || "Unknown error"}`);
		} finally {
			setLoading(false);
		}
	};

	const isDownloadDisabled = !selectedUser || !startDate || !endDate || loading || loadingTeamMembers;

	const clearMessages = () => {
		setError(null);
		setSuccessMessage(null);
	};

	return {
		isSuperUser,
		teamMembers,
		selectedUser,
		setSelectedUser,
		startDate,
		setStartDate,
		endDate,
		setEndDate,
		pastHours,
		handlePastHoursChange,
		loading,
		loadingTeamMembers,
		error,
		successMessage,
		handleDownload,
		isDownloadDisabled,
		clearMessages,
	};
};
